import React, { useState, forwardRef, useCallback, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import PropTypes from "prop-types";
import cloneDeep from "lodash/cloneDeep";

import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import MuiDialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import Zoom from "@mui/material/Zoom";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import { CONSTANT_HIDDEN_COLUMNS } from "components/CasesTable/CaseManagementTableColumns";

import { styled } from "@mui/material/styles";

const getItemStyle = (isDragging, draggableStyle) => ({
  ...draggableStyle,
  ...(isDragging && {
    background: "#A5D7A7"
  })
});

// eslint-disable-next-line react/display-name
const Transition = forwardRef((props, ref) => <Zoom ref={ref} {...props} />);

export function CaseTableSettings(props) {
  const { open, setCaseTableSettingsOpen, setColumns, columns } = props;

  const [allColumns, setAllColumns] = useState([]);

  const setFilteredColumns = useCallback(() => {
    const filteredColumns = Array.from(columns).filter(
      ({ id }) => !CONSTANT_HIDDEN_COLUMNS.includes(id)
    );

    setAllColumns(filteredColumns);
  }, [columns]);

  useEffect(() => {
    setFilteredColumns();
  }, [setFilteredColumns]);

  const reorderColumns = (columns, startIndex, endIndex) => {
    const updatedColumns = Array.from(columns);
    const [removed] = updatedColumns.splice(startIndex, 1);
    updatedColumns.splice(endIndex, 0, removed);

    return updatedColumns;
  };

  const onDragEnd = useCallback(
    result => {
      if (!result.destination) {
        return;
      }

      const items = reorderColumns(
        allColumns,
        result.source.index,
        result.destination.index
      );

      setAllColumns(items);
    },
    [allColumns]
  );

  const onColumnSelected = useCallback(
    columnId => {
      const updatedColumns = cloneDeep(allColumns);
      const index = allColumns.findIndex(({ id }) => columnId === id);
      updatedColumns[index].isVisible = !updatedColumns[index].isVisible;

      setAllColumns(updatedColumns);
    },
    [allColumns]
  );

  const onApply = useCallback(() => {
    const hiddenColumns = allColumns
      .filter(({ isVisible }) => !isVisible)
      .map(({ id }) => id);

    const columnOrder = allColumns.map(({ id }) => id);

    setColumns(columnOrder, [...hiddenColumns, ...CONSTANT_HIDDEN_COLUMNS]);
    setCaseTableSettingsOpen(false);
  }, [allColumns, setCaseTableSettingsOpen, setColumns]);

  const onClose = () => {
    setFilteredColumns();
    setCaseTableSettingsOpen(false);
  };

  return (
    <Dialog
      fullWidth={true}
      maxWidth="sm"
      open={open}
      TransitionComponent={Transition}
      keepMounted
    >
      <MuiDialogTitle>
        <DialogHeader>Table Configuration</DialogHeader>
        <DialogCloseIcon onClick={onClose}>
          <CloseIcon />
        </DialogCloseIcon>
        <DialogSubTitle>
          Select which items display on your data table and reorder by dragging
          and dropping items.
        </DialogSubTitle>
      </MuiDialogTitle>
      <DialogBody dividers>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {provided => (
              <List {...provided.droppableProps} ref={provided.innerRef}>
                {allColumns.map((column, index) => (
                  <Draggable
                    key={column.id}
                    draggableId={column.id}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <ListItem
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(
                          snapshot.isDragging,
                          provided.draggableProps.style
                        )}
                      >
                        <ListItemIcon>
                          <Checkbox
                            color="primary"
                            checked={column.isVisible}
                            disabled={column.id === "display_id"}
                            onChange={() => onColumnSelected(column.id)}
                          />
                        </ListItemIcon>
                        <ListItemText primary={column.title} />
                        <DragHandleIcon />
                      </ListItem>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      </DialogBody>
      <DialogButtonsContainer>
        <Button variant="outlined" color="primary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={onApply}>
          Apply
        </Button>
      </DialogButtonsContainer>
    </Dialog>
  );
}

const DialogHeader = styled(Typography)`
  text-transform: none;
  font-weight: bold;
`;

const DialogSubTitle = styled("span")`
  font-size: 12px;
`;

const DialogCloseIcon = styled(IconButton)`
  position: absolute;
  right: 10px;
  top: 10px;
`;

const DialogBody = styled(DialogContent)`
  max-height: 500px;
`;

const DialogButtonsContainer = styled(DialogActions)`
  justify-content: center;
  padding: 20px;
`;

CaseTableSettings.propTypes = {
  open: PropTypes.bool.isRequired,
  setCaseTableSettingsOpen: PropTypes.func.isRequired,
  setColumns: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired
};
